import {
    getNextShipmentReferenceForBrand,
    getSiteDestinationsForBrand,
    createShipment,
    declareOnBehalf,
    getSiteCatalog,
    getSiteOrdersForBrand,
    getSiteStock,
    previewDeclarationOnBehalf,
} from "@api/cm-sites";
import {
    declareProduction,
    dispatchFromSite,
    getBucketedStock,
    getNextShipmentReference,
    getSiteDestinations,
    getSiteOrders,
    getVendorCatalog,
    previewDeclaration,
    type DeclarationBlocked,
    type DeclarationPreview,
    type DeclarationRecorded,
    type DeclarationRequest,
    type DispatchResult,
    type DispatchShipmentRequest,
    type SiteDestination,
    type SiteOrder,
    type VendorCatalog,
    type BucketedItemBalance,
} from "@api/vendor-portal";

/**
 * Who is writing, and therefore which door the form uses.
 *
 * Null means the manufacturer, in its own portal, writing about itself. Set means the brand,
 * in its own session, recording what a site reported — and then every call carries the site and
 * the name of whoever at the site said it.
 *
 * ONE FORM, TWO DOORS, resolved here rather than branched inside each screen. The alternative
 * is a copy of the declaration form for the brand, and the day the two copies drifted the brand
 * would be able to record something its own manufacturer could not — which is precisely the
 * asymmetry the whole module exists to avoid.
 *
 * DISPATCH IS BACK, and it is here because both sides do it now. It was taken off this
 * interface when releasing finished goods became the brand's alone, precisely so that a screen
 * wiring a Send button would not compile — which is the tripwire this change is stepping over
 * deliberately.
 *
 * WHAT MAKES IT SAFE IS UNCHANGED: finished-goods QC is still the brand's signature, and the
 * queue offers only PASSED batches. A site sends what the brand has already released and
 * nothing else; what it gains is saying when the lorry went.
 *
 * NO REPORTER TRAVELS WITH IT, on either door. The brand's records the brand user, the site's
 * records the site login, and each door knows which without being told.
 */
export interface OnBehalfOf {
    warehouseId: string;
    reportedBy: string;
}

export interface PortalApi {
    getCatalog: () => Promise<VendorCatalog>;
    getStock: (body: {
        itemType?: "material" | "product";
    }) => Promise<{ items: BucketedItemBalance[] }>;
    preview: (request: DeclarationRequest) => Promise<DeclarationPreview>;
    declare: (
        request: DeclarationRequest,
    ) => Promise<DeclarationRecorded | DeclarationBlocked>;
    dispatch: (request: DispatchShipmentRequest) => Promise<DispatchResult>;
    /** The site's open orders, for saying which one a consignment fills. */
    getOrders: () => Promise<{ orders: SiteOrder[] }>;
    /** Where the goods may go: the brand's own warehouses, lane-backed ones first. */
    getDestinations: () => Promise<SiteDestination[]>;
    /** What an unnamed load will be called, so the form can show it before saving. */
    getNextReference: () => Promise<{ reference: string }>;
}

export function portalApi(onBehalfOf?: OnBehalfOf | null): PortalApi {
    if (!onBehalfOf) {
        return {
            getCatalog: getVendorCatalog,
            getStock: (body) => getBucketedStock(body),
            preview: previewDeclaration,
            declare: declareProduction,
            dispatch: dispatchFromSite,
            getOrders: getSiteOrders,
            getDestinations: getSiteDestinations,
            getNextReference: getNextShipmentReference,
        };
    }

    const { warehouseId } = onBehalfOf;

    return {
        getCatalog: () => getSiteCatalog(warehouseId),
        getStock: (body) => getSiteStock(warehouseId, body),
        preview: (request) => previewDeclarationOnBehalf(warehouseId, request),
        declare: (request) => declareOnBehalf(onBehalfOf, request),
        dispatch: (request) => createShipment(warehouseId, request),
        getOrders: () => getSiteOrdersForBrand(warehouseId),
        getDestinations: () => getSiteDestinationsForBrand(warehouseId),
        getNextReference: () => getNextShipmentReferenceForBrand(warehouseId),
    };
}

/** A stable cache key, so a brand looking at two sites does not read one's data for the other. */
export function portalScope(onBehalfOf?: OnBehalfOf | null): string {
    return onBehalfOf ? `site:${onBehalfOf.warehouseId}` : "self";
}
